import { connect } from 'react-redux';
import type { NavigateFunction } from 'react-router';
import {
    autofill,
    formValueSelector as formValueSelectorCreator,
    isInvalid,
    isPristine,
    isSubmitting,
    submit,
} from 'redux-form';

import type { Dispatch, State } from '../../../types';
import { submitCreateGeofence } from '../../actions/geofenceActions';
import { setMapCenter } from '../../actions/mapActions';
import { showNotification } from '../../actions/notificationActions';
import { showDeletePOIDialog } from '../../actions/poiActions';
import { Routing } from '../../app/Routing';
import { testSelfIntersection } from '../../utils/isPolygonSelfIntersecting';
import type { FunctionProperties, NonFunctionProperties } from '../../utils/typescriptutils/typeManipulation';
import { getAddressFromQueryString } from '../common/getAddressFromQueryString';
import { geofenceFormElements } from './GeofenceForm';
import { type GeofenceCreationSidebarProps, GeofenceSidebar } from './GeofenceSidebar';

export const mapStateToProps = (state: State): NonFunctionProperties<GeofenceCreationSidebarProps> => {
    const formValueSelector = formValueSelectorCreator(geofenceFormElements.geofenceFormName);
    const shape = formValueSelector(state, geofenceFormElements.geofenceShape);
    const points = formValueSelector(state, geofenceFormElements.geofencePoints);

    const validateNumberOfPointsOnMap = () => {
        if (shape === 'polygonal') {
            return points.length < 3;
        }
        return points.length < 1;
    };

    const validShape =
        !points || points.length < 4 ? true : !testSelfIntersection(points.map((p: any) => ({ x: p.lng, y: p.lat })));

    const submitDisabled =
        isPristine(geofenceFormElements.geofenceFormName)(state) ||
        isInvalid(geofenceFormElements.geofenceFormName)(state) ||
        isSubmitting(geofenceFormElements.geofenceFormName)(state) ||
        !validShape ||
        validateNumberOfPointsOnMap();

    return {
        submitDisabled,
        mode: 'CREATE' as const,
        validShape,
    };
};

export const mapDispatchToProps = (dispatch: Dispatch): FunctionProperties<GeofenceCreationSidebarProps> => ({
    autofillFromQueryString: (search: string) => {
        const address = getAddressFromQueryString(search);
        if (address) {
            dispatch(setMapCenter(address.position));
            dispatch(
                autofill(geofenceFormElements.geofenceFormName, geofenceFormElements.geofenceAddress, address.label)
            );
            dispatch(
                autofill(geofenceFormElements.geofenceFormName, geofenceFormElements.geofencePoints, [
                    { lat: address.position.latitude, lng: address.position.longitude },
                ])
            );
        }
    },
    onClose: (navigate: NavigateFunction) => {
        navigate(Routing.poiList);
    },
    onDelete: (geofenceId: string) => dispatch(showDeletePOIDialog(geofenceId)),
    onSubmitForm: (navigate: NavigateFunction) =>
        dispatch(submitCreateGeofence())
            .then(() => navigate(Routing.poiList))
            .then(() => dispatch(showNotification('intl-msg:geofence.create.successNotification', 'success')))
            .catch(() => dispatch(showNotification('intl-msg:geofence.create.errorNotification', 'error'))),
    onClickSubmit: () => dispatch(submit(geofenceFormElements.geofenceFormName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GeofenceSidebar);
